import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Calendar, DollarSign, Building2, FileText } from "lucide-react";
import type { Contract } from "@/lib/types/contracts";
import { ContractStatus } from "@/lib/types/enums";
import { ActiveStatus } from "@/lib/types/enums";

interface ContractDetailsProps {
  contract: Contract;
  onBack: () => void;
  onEdit: () => void;
  onDownloadDocument?: (documentUrl: string) => void;
}

export function ContractDetails({ contract, onBack, onEdit, onDownloadDocument }: ContractDetailsProps) {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{contract.title}</h1>
            <Badge variant={contract.status === ContractStatus.ACTIVE ? 'default' : 'secondary'} className="mt-2">
              {contract.status}
            </Badge>
          </div>
        </div>
        <Button onClick={onEdit} className="w-full sm:w-auto">
          Edit Contract
        </Button>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="details" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="media">Media & Documents</TabsTrigger>
          <TabsTrigger value="sharing">Sharing</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          {/* Key Info Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="p-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm font-medium">Term</span>
                </div>
                <div>
                  <div className="text-2xl font-bold">{contract.termLength}</div>
                  <div className="text-sm text-muted-foreground">month(s)</div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <DollarSign className="h-4 w-4" />
                  <span className="text-sm font-medium">Value</span>
                </div>
                <div>
                  <div className="text-2xl font-bold">${contract.value.toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground">total value</div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  <span className="text-sm font-medium">Client</span>
                </div>
                <div>
                  <div className="text-2xl font-bold">{contract.clientId}</div>
                  <div className="text-sm text-muted-foreground">client ID</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Contract Services */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Contract Services</h2>
            <div className="space-y-4">
              {contract.services.map((service) => (
                <div
                  key={service.id}
                  className="flex items-center justify-between p-4 rounded-lg border"
                >
                  <div>
                    <h3 className="font-medium">{service.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {service.description}
                    </p>
                    <Badge variant="outline" className="mt-2">
                      {service.frequency}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">
                      ${service.price.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      per {service.frequency}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="media">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">Documents</h2>
              <Button>
                <FileText className="h-4 w-4 mr-2" />
                Upload Document
              </Button>
            </div>
            <div className="space-y-4">
              {contract.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{doc.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {onDownloadDocument && (
                    <Button variant="outline" size="sm" onClick={() => onDownloadDocument(doc.url)}>
                      Download
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="sharing">
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Shared Access</h2>
            {contract.sharedWith ? (
              <div className="space-y-4">
                {contract.sharedWith.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-4 rounded-lg border"
                  >
                    <div>
                      <p className="font-medium">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline">{user.role}</Badge>
                        <Badge variant={user.status === ActiveStatus.ACTIVE ? 'default' : 'secondary'}>
                          {user.status}
                        </Badge>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      Revoke Access
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                No users have been granted access to this contract.
              </p>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}